import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dices, Rocket, CircleDot, Disc3, Bomb, Gift, Flame, Sparkles, Store, Spade, Circle, Layers, CalendarDays, Trophy, Crown, Gem, Target, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { DailyWordVolt } from "@/components/DailyWordVolt";
import { DuelArena } from "@/pages/Duels";
import { VoltSay } from "@/components/Volt";
import PuzzleLineup from "@/components/PuzzleLineup";
import { useAudio } from "@/context/GameFeel";
import { greeting } from "@/lib/volt";

const GAMES = [
  { to: "/casino/slot", name: "Neon Slots", icon: Dices, desc: "3-reel classic", grad: "from-amber-500 to-orange-600", testid: "game-card-slot" },
  { to: "/casino/crash", name: "Crash", icon: Rocket, desc: "Lock in before the bust", grad: "from-pink-500 to-purple-600", testid: "game-card-crash" },
  { to: "/casino/plinko", name: "Plinko", icon: CircleDot, desc: "Drop chips, watch them bounce", grad: "from-purple-500 to-indigo-600", testid: "game-card-plinko" },
  { to: "/casino/wheel", name: "Fortune Wheel", icon: Disc3, desc: "Spin the wheel", grad: "from-amber-400 to-pink-600", testid: "game-card-wheel" },
  { to: "/casino/mines", name: "Mines", icon: Bomb, desc: "Reveal gems, dodge bombs", grad: "from-fuchsia-500 to-amber-500", testid: "game-card-mines" },
  { to: "/casino/blackjack", name: "Blackjack", icon: Spade, desc: "Beat the dealer to 21", grad: "from-emerald-600 to-teal-700", testid: "game-card-blackjack" },
  { to: "/casino/roulette", name: "Roulette", icon: Circle, desc: "Red, black or your number", grad: "from-red-600 to-purple-700", testid: "game-card-roulette" },
  { to: "/casino/baccarat", name: "Baccarat", icon: Layers, desc: "Player, banker or tie", grad: "from-amber-600 to-orange-700", testid: "game-card-baccarat" },
  { to: "/casino/crashmines", name: "Crash Mines", icon: Bomb, desc: "Crash × Mines", grad: "from-fuchsia-500 to-purple-700", testid: "game-card-crashmines" },
  { to: "/casino/wheelslots", name: "Wheel Slots", icon: Disc3, desc: "Slots × Wheel", grad: "from-amber-500 to-fuchsia-600", testid: "game-card-wheelslots" },
];

export default function Casino() {
  const { user, setUser, refresh } = useAuth();
  const { t } = useT();
  const { sfx } = useAudio();
  const [hello] = useState(() => greeting(user));
  const [claiming, setClaiming] = useState(false);
  const [showDaily, setShowDaily] = useState(false);
  const [daily, setDaily] = useState(null);
  const [quests, setQuests] = useState([]);
  const [vip, setVip] = useState(null);
  const [bonusPreview, setBonusPreview] = useState(null);
  const [arcade, setArcade] = useState(null); // free skill arcade game id (practice, stays on the Play page)
  const loadDaily = () => api.get("/duels/daily").then(({ data }) => setDaily(data)).catch(() => {});
  const loadRewards = () => {
    api.get("/quests").then(({ data }) => setQuests(data.quests)).catch(() => {});
    api.get("/loyalty").then(({ data }) => setVip(data)).catch(() => {});
    api.get("/games/daily-bonus/preview").then(({ data }) => setBonusPreview(data)).catch(() => {});
  };
  const claimQuest = async (id) => {
    try { const { data } = await api.post(`/quests/${id}/claim`); await refresh(); sfx("coin"); toast.success(`Quest complete · +${data.reward.toLocaleString()} coins!`); loadRewards(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { loadDaily(); loadRewards(); }, []);

  const claimBonus = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/games/daily-bonus");
      setUser((u) => ({ ...u, coins: data.coins, streak_days: data.streak, last_bonus_date: new Date().toISOString().slice(0, 10) }));
      sfx("coin"); if (data.streak >= 3) setTimeout(() => sfx("streak"), 300);
      toast.success(`Daily bonus claimed! +${data.reward.toLocaleString()} coins · ${data.streak}-day streak 🔥`);
      loadRewards();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setClaiming(false);
    }
  };

  const claimedToday = user?.last_bonus_date === new Date().toISOString().slice(0, 10);
  const xpToNext = Math.pow((user?.level ?? 1) / 1 - 0, 1 / 0.7);
  const levelFloor = Math.floor(Math.pow(Math.max(0, (user?.level ?? 1) - 1), 1 / 0.7) * 1) * 1000;
  const progress = Math.min(100, (((user?.xp ?? 0) % 1000) / 1000) * 100);

  return (
    <div className="p-4 lg:p-8">
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden mb-8 border border-amber-500/20 glow-coins">
        <img decoding="async" fetchPriority="high" src="https://images.unsplash.com/photo-1604028296525-8304e1a4969f?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 p-6 lg:p-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> PhonePlay · {t("coins_only_no_cash")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            <span className="text-gradient-coins">PhonePlay</span> — {t("social_casino")}
          </h1>
          <p className="text-gray-400 max-w-xl mb-6">{t("casino_sub")}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/store" data-testid="coin-store-trigger-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold hover:opacity-90 transition-opacity duration-200">
              <Store className="w-4 h-4" /> {user?.coin_store_enabled ? t("coin_store") : t("free_coins")}
            </Link>
          </div>
        </div>
      </div>

      {/* Volt greeting */}
      <div className="mb-8"><VoltSay testid="volt-home-greeting" text={hello} mood={(user?.streak_days || 0) >= 3 ? "cheer" : "happy"} size={72} /></div>

      <PuzzleLineup streak={user?.puzzle_streak || 0} />

      {/* Daily WordVolt banner */}
      {daily && (
        <button data-testid="home-daily-banner" onClick={() => setShowDaily(true)}
          className="w-full text-left rounded-3xl overflow-hidden border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-purple-600/10 to-transparent hover:border-amber-500/50 transition-colors duration-200 p-5 lg:p-6 mb-8 flex items-center gap-4 group">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0">
            <CalendarDays className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg font-black flex items-center gap-2">Daily WordVolt <Sparkles className="w-4 h-4 text-amber-400" />
              {daily.streak > 0 && <span className="inline-flex items-center gap-1 text-xs text-orange-300"><Flame className="w-3.5 h-3.5" />{daily.streak}</span>}
            </p>
            <p className="text-sm text-gray-400 truncate">
              {daily.played
                ? <>You ranked <span className="text-amber-300 font-semibold">#{daily.your_rank}</span> of {daily.total_players} today · {daily.your_score} pts</>
                : "Today's puzzle is live — same letters for everyone. One free shot!"}
            </p>
          </div>
          <div className="hidden sm:flex gap-1.5 mr-2">
            {daily.rack.slice(0, 7).map((ch, i) => (
              <span key={i} className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-sm font-black text-amber-100">{ch}</span>
            ))}
          </div>
          <span className="inline-flex px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold text-sm group-hover:opacity-90 whitespace-nowrap">{daily.played ? "View" : "Play"}</span>
        </button>
      )}

      {/* Daily Quests */}
      {quests.length > 0 && (
        <div className="mb-10" data-testid="quests-section">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-6 h-6 text-emerald-400" />
            <div>
              <h2 className="font-display text-2xl font-bold">Daily Quests</h2>
              <p className="text-sm text-gray-500">Complete today's goals for bonus coins — resets at midnight.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quests.map((q) => (
              <div key={q.id} data-testid={`quest-${q.id}`} className="glass rounded-2xl p-5 border border-white/10 flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-sm">{q.title}</p>
                  {q.claimed ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <span className="text-xs text-amber-300 font-mono shrink-0">+{q.reward.toLocaleString()}</span>}
                </div>
                <p className="text-xs text-gray-500 mb-3 flex-1">{q.desc}</p>
                <div className="h-2 rounded-full bg-black/40 overflow-hidden mb-3">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-500" style={{ width: `${Math.round(q.progress / q.target * 100)}%` }} />
                </div>
                <button data-testid={`quest-claim-${q.id}`} onClick={() => claimQuest(q.id)} disabled={!q.completed || q.claimed}
                  className={`w-full py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${q.claimed ? "bg-white/5 text-gray-500" : q.completed ? "bg-gradient-to-r from-emerald-500 to-cyan-600" : "bg-white/5 text-gray-500"}`}>
                  {q.claimed ? "Claimed" : q.completed ? "Claim reward" : `${q.progress}/${q.target}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIP Loyalty */}
      {vip && (
        <div className="mb-10 glass rounded-3xl p-6 border border-amber-500/25 bg-gradient-to-br from-amber-500/8 to-purple-600/8" data-testid="vip-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold">VIP · <span className="text-amber-300" data-testid="vip-tier">{vip.tier}</span></h2>
                <p className="text-sm text-gray-500">{vip.points.toLocaleString()} loyalty points · earn more on every play & purchase</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-500">Daily bonus perk</span>
              <p className="font-mono font-bold text-emerald-300">{vip.multiplier}×</p>
            </div>
          </div>
          {vip.next_tier && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{vip.tier}</span><span>{vip.next_tier} · {vip.next_at.toLocaleString()} pts</span></div>
              <div className="h-2.5 rounded-full bg-black/40 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-purple-500 transition-all duration-500" style={{ width: `${vip.progress_pct}%` }} />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {vip.tiers.map((tr, i) => (
              <div key={tr.name} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${tr.name === vip.tier ? "border-amber-500/50 bg-amber-500/15 text-amber-200" : i < vip.tier_index ? "border-emerald-500/30 text-emerald-300/70" : "border-white/10 text-gray-500"}`}>
                {tr.name} <span className="opacity-60">{tr.multiplier}×</span>
              </div>
            ))}
          </div>
          <ul className="mt-4 text-sm text-gray-400 space-y-1">
            {vip.perks.map((p, i) => <li key={i} className="flex items-center gap-2"><Gem className="w-3.5 h-3.5 text-amber-400" /> {p}</li>)}
          </ul>
        </div>
      )}

      {/* Daily bonus + level */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="glass rounded-2xl p-6 border border-amber-500/20 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-amber-400 mb-1"><Gift className="w-5 h-5" /><span className="font-semibold">{t("daily_bonus")}</span></div>
            <p className="text-sm text-gray-500">{t("daily_bonus_sub")}</p>
            {bonusPreview && !claimedToday && (
              <p data-testid="daily-bonus-amount" className="text-xs text-amber-300 font-mono mt-1">
                Today: +{bonusPreview.reward.toLocaleString()} coins{bonusPreview.vip_multiplier > 1 ? ` · VIP ${bonusPreview.vip_multiplier}×` : ""}
              </p>
            )}
          </div>
          <button data-testid="claim-daily-bonus-button" onClick={claimBonus} disabled={claiming || claimedToday}
            className="px-5 py-2.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 font-semibold hover:bg-amber-500/30 transition-colors duration-200 disabled:opacity-40">
            {claimedToday ? t("claimed") : claiming ? "…" : bonusPreview ? `${t("claim")} +${bonusPreview.reward.toLocaleString()}` : t("claim")}
          </button>
        </div>
        <div className="glass rounded-2xl p-6 border border-white/10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-orange-500/15 flex items-center justify-center"><Flame className="w-6 h-6 text-orange-400" /></div>
          <div><p className="text-2xl font-display font-black">{user?.streak_days ?? 0}</p><p className="text-sm text-gray-500">{t("day_streak")}</p></div>
        </div>
        <div className="glass rounded-2xl p-6 border border-white/10">
          <div className="flex justify-between mb-2"><span className="font-semibold">{t("level")} {user?.level ?? 1}</span><span className="text-sm text-gray-500 font-mono">{(user?.xp ?? 0).toLocaleString()} XP</span></div>
          <div className="h-2.5 rounded-full bg-black/50 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-purple-500 transition-all duration-500" style={{ width: `${progress}%` }} /></div>
        </div>
      </div>

      {/* Casino-style games + skill mini-games — one labeled section at the very bottom of the lobby */}
      <section data-testid="casino-games-section" className="mt-12 pt-8 border-t border-white/10">
        <div className="flex items-center gap-3 mb-5">
          <Dices className="w-6 h-6 text-amber-400" />
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">{t("fun_casino")}</h2>
            <p className="text-sm text-gray-500">{t("fun_casino_sub")}</p>
          </div>
        </div>
        <div data-testid="casino-lobby-section" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {GAMES.map((g, i) => (
            <motion.div key={g.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Link to={g.to} data-testid={g.testid}
                className="group block relative rounded-2xl overflow-hidden border border-white/10 hover:border-amber-500/40 transition-colors duration-200 h-52">
                <div className={`absolute inset-0 bg-gradient-to-br ${g.grad} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${g.grad} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <g.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold">Coins Only</span>
                    <h3 className="font-display text-2xl font-bold mt-1">{g.name}</h3>
                    <p className="text-sm text-gray-400">{g.desc}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
          {[
            { game: "skill_reels", name: "Skill Reels", desc: "Nudge the reels to line up the target.", icon: Disc3, grad: "from-amber-500 to-orange-600", testid: "arcade-skill_reels" },
            { game: "precision_wheel", name: "Precision Wheel", desc: "Stop the pointer exactly on target.", icon: CircleDot, grad: "from-purple-500 to-fuchsia-600", testid: "arcade-precision_wheel" },
            { game: "card_21", name: "Card 21", desc: "Pick cards that total exactly 21.", icon: Layers, grad: "from-rose-500 to-amber-600", testid: "arcade-card_21" },
          ].map((g, i) => (
            <motion.div key={g.game} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (GAMES.length + i) * 0.06 }}>
              <button type="button" onClick={() => setArcade(g.game)} data-testid={g.testid}
                className="group block w-full text-left relative rounded-2xl overflow-hidden border border-white/10 hover:border-cyan-500/40 transition-colors duration-200 h-52">
                <div className={`absolute inset-0 bg-gradient-to-br ${g.grad} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${g.grad} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <g.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-cyan-400/80 font-semibold">Free Play · Coins Only</span>
                    <h3 className="font-display text-2xl font-bold mt-1">{g.name}</h3>
                    <p className="text-sm text-gray-400">{g.desc}</p>
                  </div>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {showDaily && <DailyWordVolt onClose={() => { setShowDaily(false); loadDaily(); }} />}
      {arcade && <DuelArena duel={null} opponent={{ name: "Practice", elo: "—" }} practice game={arcade}
        onClose={() => { setArcade(null); loadRewards(); }} refresh={refresh} onRematch={() => {}} onChallenge={() => {}} />}
    </div>
  );
}
